/**
 * Chakra UI Custom Theme Configuration
 *
 * This file defines the complete theme configuration for the Xacto App.
 * It extends Chakra UI's default theme with custom colors, fonts,
 * breakpoints, and component styles.
 *
 * @fileoverview Main theme configuration for Chakra UI v2
 *
 * @example
 * // Import and use in ChakraProvider
 * import { theme } from './theme';
 * <ChakraProvider theme={theme}>
 *   <App />
 * </ChakraProvider>
 */

import { extendTheme, type ThemeConfig } from '@chakra-ui/react';
import { components } from './components';

/**
 * Theme Configuration Options
 *
 * Controls the initial color mode and system preference behavior.
 * - initialColorMode: The default color mode when app first loads
 * - useSystemColorMode: Whether to respect OS dark/light mode preference
 *
 * Color mode is persisted in localStorage by default.
 */
const config: ThemeConfig = {
  // Start in dark mode - Modern dark theme is now the default
  initialColorMode: 'dark',
  // Disable system preference to enforce consistent dark theme
  useSystemColorMode: false,
};

/**
 * Modern Dark Color Palette (Xacto Brand)
 *
 * A modern, dark-first color palette inspired by Solana's design system.
 * Features deep navy backgrounds with cyan/purple accent gradients.
 *
 * Base color: #171E26 (Deep Navy)
 */
const colors = {
  /**
   * Xacto Modern Dark Palette
   */
  xacto: {
    // Base colors (Dark Theme Foundation)
    deepNavy: '#171E26',      // Primary background
    darkSurface: '#1E2733',   // Card backgrounds, elevated surfaces
    mutedNavy: '#2A3544',     // Borders, dividers, input backgrounds

    // Accent colors (Solana-inspired gradients)
    electricCyan: '#00D9FF',  // Primary accent, buttons, links
    vibrantTeal: '#00FFA3',   // Success highlights
    softPurple: '#9945FF',    // Secondary accent, gradients

    // Text colors
    pureWhite: '#FFFFFF',     // Headings, primary text
    silverGray: '#A3B1C6',    // Body text, descriptions
    mutedGray: '#6B7A8F',     // Placeholder text, disabled states
  },

  /**
   * Brand colors - mapped to xacto palette for compatibility
   */
  brand: {
    // Primary color - Deep navy for backgrounds
    primary: '#171E26',
    primaryLight: '#1E2733',
    primaryDark: '#0F141A',

    // Secondary color - Electric cyan for interactive elements
    secondary: '#00D9FF',
    secondaryLight: '#33E1FF',
    secondaryDark: '#00B8D9',

    // Accent color - Vibrant teal for success/highlights
    accent: '#00FFA3',
    accentLight: '#33FFB5',
    accentDark: '#00CC82',

    // Gradient accent - Soft purple
    gradient: '#9945FF',
    gradientLight: '#B366FF',
    gradientDark: '#7A35CC',
  },

  /**
   * Extended gray palette - Dark theme optimized
   */
  gray: {
    50: '#E8ECF0',
    100: '#C5CDD6',
    200: '#A3B1C6',
    300: '#8494A8',
    400: '#6B7A8F',
    500: '#4A5568',
    600: '#2A3544',
    700: '#1E2733',
    800: '#171E26',
    900: '#0F141A',
  },

  /**
   * Semantic colors for status indicators - Dark theme optimized
   */
  success: {
    50: '#0A2A1A',
    100: '#0F3D26',
    500: '#00FFA3',
    600: '#00CC82',
  },
  error: {
    50: '#2A0F0F',
    100: '#3D1515',
    500: '#FF4D4D',
    600: '#E53E3E',
  },
  warning: {
    50: '#2A1F0F',
    100: '#3D2D15',
    500: '#FFB020',
    600: '#DD9B10',
  },
  info: {
    50: '#0F1A2A',
    100: '#15253D',
    500: '#00D9FF',
    600: '#00B8D9',
  },
};

/**
 * Typography Configuration
 *
 * Defines the font families used throughout the application.
 * Inter is used for both headings and body text for consistency.
 *
 * Font stack includes fallbacks for systems without Inter installed.
 */
const fonts = {
  // Heading font - Used for h1-h6, titles, prominent text
  heading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',

  // Body font - Used for paragraphs, labels, general text
  body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',

  // Monospace font - Used for code, technical content
  mono: '"Fira Code", "Fira Mono", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
};

/**
 * Font Size Scale
 *
 * Custom font sizes that provide good visual hierarchy.
 * Based on a modular scale for harmonious proportions.
 */
const fontSizes = {
  xs: '0.75rem',    // 12px - Small labels, captions
  sm: '0.875rem',   // 14px - Secondary text, form labels
  md: '1rem',       // 16px - Body text (base)
  lg: '1.125rem',   // 18px - Lead paragraphs
  xl: '1.25rem',    // 20px - Section headers
  '2xl': '1.5rem',  // 24px - Page titles
  '3xl': '1.875rem', // 30px - Major headings
  '4xl': '2.25rem', // 36px - Hero headings
  '5xl': '3rem',    // 48px - Display text
  '6xl': '3.75rem', // 60px - Large display
};

/**
 * Breakpoints for Responsive Design
 *
 * These breakpoints define when the layout should adapt to different
 * screen sizes. They follow a mobile-first approach.
 *
 * Usage in components:
 * ```tsx
 * <Box fontSize={{ base: 'sm', md: 'md', lg: 'lg' }} />
 * ```
 */
const breakpoints = {
  base: '0em',      // 0px - Mobile first (default)
  sm: '30em',       // 480px - Large phones
  md: '48em',       // 768px - Tablets
  lg: '62em',       // 992px - Small laptops
  xl: '80em',       // 1280px - Desktops
  '2xl': '96em',    // 1536px - Large screens
};

/**
 * Spacing Scale
 *
 * Consistent spacing values for margins, paddings, and gaps.
 * Based on a 4px grid system for visual consistency.
 */
const space = {
  px: '1px',
  0.5: '0.125rem',  // 2px
  1: '0.25rem',     // 4px
  1.5: '0.375rem',  // 6px
  2: '0.5rem',      // 8px
  2.5: '0.625rem',  // 10px
  3: '0.75rem',     // 12px
  3.5: '0.875rem',  // 14px
  4: '1rem',        // 16px
  5: '1.25rem',     // 20px
  6: '1.5rem',      // 24px
  7: '1.75rem',     // 28px
  8: '2rem',        // 32px
  9: '2.25rem',     // 36px
  10: '2.5rem',     // 40px
  12: '3rem',       // 48px
  14: '3.5rem',     // 56px
  16: '4rem',       // 64px
  20: '5rem',       // 80px
  24: '6rem',       // 96px
  28: '7rem',       // 112px
  32: '8rem',       // 128px
};

/**
 * Border Radius Scale
 *
 * Consistent border radius values for rounded corners.
 */
const radii = {
  none: '0',
  sm: '0.125rem',   // 2px - Subtle rounding
  base: '0.25rem',  // 4px - Default rounding
  md: '0.375rem',   // 6px - Medium rounding
  lg: '0.5rem',     // 8px - Large rounding
  xl: '0.75rem',    // 12px - Extra large
  '2xl': '1rem',    // 16px - Very rounded
  '3xl': '1.5rem',  // 24px - Pill-like
  full: '9999px',   // Fully rounded (circles)
};

/**
 * Box Shadow Scale
 *
 * Elevation shadows for depth and visual hierarchy.
 * Optimized for dark theme with cyan glow accents.
 */
const shadows = {
  xs: '0 0 0 1px rgba(0, 217, 255, 0.05)',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  // Custom shadows for dark mode with cyan glow
  'dark-lg': '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.4)',
  // Cyan glow shadow for cards and elevated elements
  'cyan-glow': '0 4px 30px rgba(0, 217, 255, 0.15)',
  'cyan-glow-lg': '0 8px 40px rgba(0, 217, 255, 0.2)',
  // Purple glow for special elements
  'purple-glow': '0 4px 30px rgba(153, 69, 255, 0.15)',
  // Focus ring shadow - cyan accent
  outline: '0 0 0 3px rgba(0, 217, 255, 0.4)',
  // Inner shadow
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.3)',
  none: 'none',
};

/**
 * Global Styles
 *
 * Global CSS applied to the entire application.
 * Uses semantic tokens for automatic light/dark mode support.
 */
const styles = {
  global: {
    // Reset and baseline styles - uses semantic tokens for color mode
    'html, body': {
      fontFamily: 'body',
      color: 'text.secondary',
      bg: 'bg.primary',
      lineHeight: 'base',
    },
    // Smooth scrolling
    html: {
      scrollBehavior: 'smooth',
    },
    // Focus visible for accessibility
    '*:focus-visible': {
      outline: '2px solid',
      outlineColor: 'accent.primary',
      outlineOffset: '2px',
    },
    // Remove default focus outline (handled by focus-visible)
    '*:focus:not(:focus-visible)': {
      outline: 'none',
    },
    // Link styles
    a: {
      color: 'accent.primary',
      _hover: {
        color: 'accent.success',
        textDecoration: 'none',
      },
    },
    // Respect the OS-level "reduce motion" preference: kills animations and
    // transitions app-wide, including the sidebar collapse/expand width and
    // margin transitions (see AppShell, Sidebar).
    '@media (prefers-reduced-motion: reduce)': {
      '*, *::before, *::after': {
        animationDuration: '0.01ms !important',
        animationIterationCount: '1 !important',
        transitionDuration: '0.01ms !important',
        scrollBehavior: 'auto !important',
      },
    },
    // Selection highlight
    '::selection': {
      bg: 'rgba(0, 217, 255, 0.3)',
      color: 'white',
    },
  },
};

/**
 * Semantic Color Tokens
 *
 * These tokens automatically switch between light and dark mode values.
 * Use these tokens in components for automatic theme-aware colors.
 * 
 * Format: { default: lightModeValue, _dark: darkModeValue }
 */
const semanticTokens = {
  colors: {
    // Background colors
    'bg.primary': { default: 'white', _dark: 'xacto.deepNavy' },
    'bg.surface': { default: 'gray.50', _dark: 'xacto.darkSurface' },
    'bg.muted': { default: 'gray.100', _dark: 'xacto.mutedNavy' },
    'bg.hover': { default: 'gray.200', _dark: 'whiteAlpha.200' },
    
    // Text colors
    'text.primary': { default: 'gray.800', _dark: 'xacto.pureWhite' },
    'text.secondary': { default: 'gray.600', _dark: 'xacto.silverGray' },
    'text.muted': { default: 'gray.500', _dark: 'xacto.mutedGray' },
    
    // Border colors
    'border.default': { default: 'gray.200', _dark: 'xacto.mutedNavy' },
    'border.muted': { default: 'gray.100', _dark: 'whiteAlpha.100' },
    
    // Accent colors (maintain brand consistency)
    'accent.primary': { default: 'blue.500', _dark: 'xacto.electricCyan' },
    'accent.success': { default: 'green.500', _dark: 'xacto.vibrantTeal' },
    'accent.purple': { default: 'purple.500', _dark: 'xacto.softPurple' },
  },
};

/**
 * Extended Theme Object
 *
 * Combines all theme configurations into a single theme object.
 * This is exported and used in the ChakraProvider.
 */
export const theme = extendTheme({
  config,
  colors,
  semanticTokens,
  fonts,
  fontSizes,
  breakpoints,
  space,
  radii,
  shadows,
  styles,
  components,
});

/**
 * Export theme type for TypeScript support
 *
 * This allows proper type inference when using theme values
 * in styled components or custom hooks.
 */
export type Theme = typeof theme;

export default theme;
